import React from 'react'
import ReactDOM from 'react-dom'
import { withDependencies, optional, multi, named } from '@wix/thunderbolt-ioc'
import {
	IRenderer,
	BatchingStrategy,
	BatchingStrategySymbol,
	LayoutDoneServiceSymbol,
	ILayoutDoneService,
	ExperimentsSymbol,
	Experiments,
	BaseComponentSymbol,
	IBaseComponent,
	SiteFeatureConfigSymbol,
	ViewerModel,
	ViewerModelSym,
} from '@wix/thunderbolt-symbols'
import { createPromise } from '@wix/thunderbolt-commons'
import type { IRendererPropsProvider, RendererProps, IThunderboltRootComponentRenderer } from '../types'
import { RendererPropsProviderSym, ThunderboltRootComponentRendererSym, name } from '../symbols'
import { ReactRendererSiteConfig } from '../types'

const { resolver: appDidMountResolver, promise: waitForAppDidMount } = createPromise()
export const appDidMountPromise = waitForAppDidMount

export const AppRootRenderer = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		RendererPropsProviderSym,
		BatchingStrategySymbol,
		BaseComponentSymbol,
		optional(LayoutDoneServiceSymbol),
	],
	(
		{ disabledComponents }: ReactRendererSiteConfig,
		rendererProps: IRendererPropsProvider,
		batchingStrategy: BatchingStrategy,
		BaseComponent: IBaseComponent,
		layoutDoneService: ILayoutDoneService
	): IThunderboltRootComponentRenderer => ({
		render: (rootCompId, key) => {
			const App = require('../components/App').default // App creates a React Context on module state, so it has to be evaluated once React is defined.
			const props = rendererProps.getRendererProps()

			return (
				<App
					key={key}
					{...props}
					batchingStrategy={batchingStrategy}
					onDidMount={appDidMountResolver}
					{...(layoutDoneService ? { layoutDoneService } : {})}
					rootCompId={rootCompId}
					BaseComponent={BaseComponent}
					disabledComponents={disabledComponents}
				/>
			)
		},
	})
)

export const ReactClientRenderer = withDependencies(
	[multi(ThunderboltRootComponentRendererSym), RendererPropsProviderSym, ExperimentsSymbol, ViewerModelSym],
	(
		renderers: Array<IThunderboltRootComponentRenderer>,
		rendererProps: IRendererPropsProvider,
		experiments: Experiments,
		viewerModel: ViewerModel
	): IRenderer<RendererProps, Promise<void>> => ({
		getRendererProps: rendererProps.getRendererProps,
		init: async () => {
			await rendererProps.resolveRendererProps()
		},
		render: async ({
			rootCompId = 'main_MF',
			target = document.getElementById('SITE_CONTAINER') as HTMLElement,
		}) => {
			if (experiments['specs.thunderbolt.skip_hydration']) {
				return
			}

			await window.reactAndReactDOMLoaded
			const app = (
				<React.StrictMode>
					<React.Fragment>
						{renderers.map((renderer, key) => renderer.render(rootCompId, key))}
					</React.Fragment>
				</React.StrictMode>
			)
			if (target.firstChild) {
				if (experiments['specs.thunderbolt.react_experimental'] && viewerModel.react18Compatible) {
					// @ts-ignore
					React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.Scheduler.unstable_forceFrameRate(22)
					// @ts-ignore
					React.startTransition(() => {
						// @ts-ignore
						ReactDOM.hydrateRoot(target, app)
					})
				} else {
					ReactDOM.hydrate(app, target)
				}
			} else {
				experiments['specs.thunderbolt.react_experimental'] && viewerModel.react18Compatible
					? // @ts-ignore
					  ReactDOM.createRoot(target).render(app)
					: ReactDOM.render(app, target)
			}
			await waitForAppDidMount
		},
	})
)
